import { useState } from 'react';
import { BarChart3, BotMessageSquare, History, KeyRound, Plus, Users } from 'lucide-react';
import { SYSTEM_PERMISSIONS } from '@masters-club/types';
import { FormHeader } from '@/components/forms';
import { TabsWithPersistence } from '@/components/shared/TabsWithPersistence';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/hooks/usePermission';
import { AuditTable } from './components/audit-table';
import { ConnectionCard } from './components/connection-card';
import { CoveragePanel } from './components/coverage-panel';
import { CreateTokenDialog } from './components/create-token-dialog';
import { TokenCreatedDialog } from './components/token-created-dialog';
import { TokensTable } from './components/tokens-table';
import { useMcpInfo } from './hooks/use-mcp';
import type { McpCreatedToken } from './types';

/**
 * Страница «MCP-доступ»: подключение ИИ-клиентов к backend через MCP,
 * персональные токены, журнал вызовов и (для mcp.manage) покрытие API.
 */
export function McpAccessPage() {
  const { hasPermission, isLoading: permissionLoading } = usePermission(SYSTEM_PERMISSIONS.MCP_USE);
  const info = useMcpInfo();
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<McpCreatedToken | null>(null);
  const canManage = info.data?.canManage === true;

  const header = (
    <FormHeader
      icon={<BotMessageSquare className="h-5 w-5" />}
      title="MCP-доступ"
      description="Подключение Claude и других ИИ-клиентов к админке. Клиент действует от вашего имени и с правами вашей роли."
    />
  );

  if (permissionLoading) {
    return (
      <div className="space-y-6">
        {header}
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="space-y-6">
        {header}
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Роли не выдано право «Подключение ИИ-клиентов (MCP)» (mcp.use). Обратитесь к администратору.
          </CardContent>
        </Card>
      </div>
    );
  }

  const createButton = (
    <Button size="sm" className="gap-1.5" onClick={() => setCreateOpen(true)} disabled={!info.data}>
      <Plus className="h-4 w-4" /> Создать токен
    </Button>
  );

  const tabs = [
    {
      value: 'tokens',
      label: 'Мои токены',
      icon: <KeyRound className="h-4 w-4" />,
      content: <TokensTable all={false} actions={createButton} />,
    },
    ...(canManage
      ? [
          {
            value: 'all-tokens',
            label: 'Все токены',
            icon: <Users className="h-4 w-4" />,
            content: <TokensTable all />,
          },
          {
            value: 'audit',
            label: 'Журнал вызовов',
            icon: <History className="h-4 w-4" />,
            content: <AuditTable all />,
          },
          {
            value: 'coverage',
            label: 'Покрытие API',
            icon: <BarChart3 className="h-4 w-4" />,
            content: <CoveragePanel enabled={canManage} />,
          },
        ]
      : [
          {
            value: 'my-audit',
            label: 'Мой журнал',
            icon: <History className="h-4 w-4" />,
            content: <AuditTable all={false} />,
          },
        ]),
  ];

  return (
    <div className="space-y-6">
      {header}
      <ConnectionCard info={info.data} isLoading={info.isLoading} error={info.error} />
      {info.data ? <TabsWithPersistence tabs={tabs} persistKey="mcp-access" /> : null}

      <CreateTokenDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        ttlOptions={info.data?.ttlOptions ?? []}
        onCreated={(token) => {
          setCreateOpen(false);
          setCreated(token);
        }}
      />
      <TokenCreatedDialog created={created} onClose={() => setCreated(null)} />
    </div>
  );
}
